/**
 * useSessionAlerts — audible + haptic "tap on the shoulder" for sessions the
 * current user has RSVPed IN for. iter114.
 *
 * Triggers at three thresholds relative to session_start_iso:
 *   T − 5 min  → silent permission ask (only if not granted yet, once per visit)
 *   T − 60s    → vibrate + browser notification "Starting in 1 minute"
 *   T − 10s    → vibrate + browser notification "Starting in 10s · Get to the court!"
 *
 * Falls back to an in-app `toast` if the Notification API is denied/unavailable.
 * Idempotent via `firedRef` — each (session_id, milestone) fires at most once.
 *
 * The hook ticks at 1s cadence ONLY when at least one watched session is
 * within 6 minutes of starting; otherwise the effect lies dormant.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const ICON_URL = '/logo192.png';

function fireHaptic() {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try { navigator.vibrate([50, 30, 50]); } catch { /* noop */ }
  }
}

function fireNotification(title, body) {
  if (typeof window === 'undefined') return;
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body,
        icon: ICON_URL,
        badge: ICON_URL,
        tag: 'session-alert',           // collapse repeat alerts
        renotify: true,
        requireInteraction: false,
        silent: false,
      });
      // Tab focus when user taps the notification.
      n.onclick = () => {
        try { window.focus(); } catch { /* noop */ }
        try { n.close(); } catch { /* noop */ }
      };
      return;
    } catch { /* fall through to toast */ }
  }
  // In-app fallback (always works even with notifications denied).
  toast(title, { description: body, duration: 8000 });
}

export default function useSessionAlerts(rail) {
  const firedRef = useRef(new Set());
  const askedRef = useRef(false);
  const [, setTick] = useState(0);

  // Determine whether we need a 1s ticker.
  const needsFastTick = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (!rail || rail.length === 0) return false;
    const now = Date.now();
    return rail.some((s) => {
      if (!s.im_in || !s.start_iso) return false;
      const delta = new Date(s.start_iso).getTime() - now;
      return delta > 0 && delta <= 360_000; // < 6 min
    });
  }, [rail]);

  useEffect(() => {
    if (!needsFastTick) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1_000);
    return () => clearInterval(id);
  }, [needsFastTick]);

  // Re-evaluate every render — the tick state forces a re-render every second
  // while needsFastTick is true. Idempotent via firedRef.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!rail || rail.length === 0) return;
    const now = Date.now();

    for (const s of rail) {
      if (!s.im_in || !s.start_iso) continue;
      const startMs = new Date(s.start_iso).getTime();
      const secs = Math.floor((startMs - now) / 1000);
      if (secs < -5) continue; // session already started (5s grace)

      // Permission ask at T-5min, once per visit.
      if (
        !askedRef.current
        && secs <= 300
        && secs > 0
        && 'Notification' in window
        && Notification.permission === 'default'
      ) {
        askedRef.current = true;
        try { Notification.requestPermission(); } catch { /* noop */ }
      }

      // T-60s alert
      const key60 = `${s.session_id}:60`;
      if (secs <= 60 && secs > 50 && !firedRef.current.has(key60)) {
        firedRef.current.add(key60);
        fireHaptic();
        fireNotification(
          `${s.title} · Starting in 1 minute`,
          `${s.group_name} · ${s.start_time}`,
        );
      }

      // T-10s alert
      const key10 = `${s.session_id}:10`;
      if (secs <= 10 && secs > 0 && !firedRef.current.has(key10)) {
        firedRef.current.add(key10);
        fireHaptic();
        fireNotification(
          `${s.title} · Starting in ${secs}s`,
          'Get to the court!',
        );
      }
    }
  });
}
